/**
 * Le moteur de calcul d'une facture de garderie (ATL) : la règle, et rien d'autre.
 *
 * Ce module ne connaît ni la base, ni l'écran. Il reçoit le catalogue d'une
 * école (plages, formules, prix), les familles (statut tarifaire), les enfants
 * (abonnement, présences, « rentre le midi », payeurs et leur part), un mois à
 * facturer, et rend les lignes de chaque facture, en centimes entiers. C'est la
 * **spécification exécutable** du calcul : la fonction SQL qui émettra les
 * factures devra rendre, sur les mêmes cas, les mêmes lignes (comme
 * `payments/jumeaux.ts` et ses jumelles SQL). Les cas sont dans
 * `__tests__/calcul.test.ts`.
 *
 * Règles (docs/garderie/CADRAGE.md, § 3 et § 6, décisions du 31/10/2026) :
 *
 * - un enfant **abonné** paie sa formule, au prix du statut tarifaire du payeur,
 *   et rien pour les plages qu'elle couvre ; le mois est dû en entier, sans
 *   prorata ;
 * - toute présence sur une plage **hors formule** est à la carte, au prix de la
 *   plage ce jour-là (le lundi CSSA coûte plus cher) ;
 * - `ATL2` **implique** `ATL1` : le même jour, l'enfant pointé aux deux ne paie
 *   que `ATL2` ;
 * - un enfant **sans formule** qui reste le midi paie `jours d'école × prix du
 *   midi` ; celui qui « rentre à la maison tous les midis » ne paie rien pour
 *   le midi ;
 * - statut `professionnel` : tout à 0 € ; statut `reduit` : le prix réduit
 *   quand l'école en a un, le prix plein sinon (ISA n'a pas de tarif réduit) ;
 * - une période **offerte** (la semaine d'août dans la facture de septembre,
 *   si Éclosion le décide) : ses présences ne sont pas facturées, elles restent
 *   des présences ;
 * - un enfant peut avoir **plusieurs payeurs**, chacun pour une part (QuickSchool
 *   connaissait le 50/50 et le 40/60) : chaque payeur reçoit sa part de chaque
 *   ligne, calculée au prix de **son** statut, arrondie au centime par la
 *   méthode du plus fort reste (la somme des parts fait toujours le total, le
 *   centime en trop va au payeur dont la part décimale est la plus grande, à
 *   égalité au premier) ; un payeur à **0 %** reste relié à l'enfant (fiscalité,
 *   visibilité) mais ne reçoit aucune ligne ; des parts qui ne font pas 100 sont
 *   signalées et rien n'est facturé pour cet enfant ;
 * - aucun montant n'est divisé hors de cette répartition : une ligne vaut
 *   quantité × prix unitaire, puis sa part ; le total est la somme des lignes ;
 *   0 → pas de facture.
 *
 * Ce que le moteur signale sans décider (`avertissements`) : une plage inconnue
 * du catalogue, une présence hors de la période, une présence un jour où la
 * plage ne tourne pas, un abonnement à une formule inconnue, des payeurs dont
 * les parts ne font pas 100, un payeur inconnu. L'administration tranche sur
 * le brouillon ; le moteur ne corrige jamais en silence.
 *
 * PROPOSITION TECHNIQUE. [cdv:garderie]
 */

/** Lundi = 1 … vendredi = 5 (le week-end n'a pas de garderie). */
export type JourOuvrable = 1 | 2 | 3 | 4 | 5;

export type StatutTarifaire = 'plein' | 'reduit' | 'professionnel';

/** Un prix en centimes, plein et, quand l'école en a un, réduit. */
export interface Prix {
  pleinCents: number;
  /** `null` : pas de tarif réduit pour cet élément — le réduit paie le plein. */
  reduitCents: number | null;
}

/** Un moment de garderie que l'animateur pointe. */
export interface Plage {
  code: string;
  libelle: string;
  /** Les jours où la plage tourne. Une présence un autre jour est signalée. */
  jours: readonly JourOuvrable[];
  prix: Prix;
  /** Un prix différent certains jours (CSSA : le lundi, sortie à 14h55). */
  prixParJour?: Partial<Record<JourOuvrable, Prix>>;
  /**
   * La plage que celle-ci implique : un enfant pointé ici l'est aussi là, et
   * ne paie que celle-ci (`ATL2` implique `ATL1`).
   */
  implique?: string;
  compteOne: boolean;
}

/** Un abonnement mensuel. */
export interface Formule {
  code: string;
  libelle: string;
  prixMensuel: Prix;
  /** Toutes les formules 2026-27 couvrent le midi ; le modèle ne l'impose pas. */
  couvreMidi: boolean;
  /** Les codes des plages couvertes. */
  plages: readonly string[];
}

export interface CatalogueEcole {
  /** Le code du centre-école (`centers.tag`). */
  ecole: string;
  /** 2026 pour 2026-2027. */
  anneeScolaire: number;
  /** Le temps de midi, par jour d'école. Pas une plage : il se compte. */
  prixMidi: Prix;
  plages: readonly Plage[];
  formules: readonly Formule[];
}

/** Une présence pointée : un ✓. */
export interface Presence {
  /** `AAAA-MM-JJ`. */
  date: string;
  plage: string;
}

/** Qui paie quelle part d'un enfant. Les parts d'un enfant font 100. */
export interface Payeur {
  familleId: string;
  /** En pour cent, entier, de 1 à 100. */
  partPct: number;
}

export interface Enfant {
  id: string;
  prenom: string;
  nom: string;
  /** Coché par l'administration : rien n'est facturé pour le midi. */
  rentreLeMidi: boolean;
  /** Le code de la formule active ce mois-ci, ou `null`. */
  formule: string | null;
  presences: readonly Presence[];
  /** Dans l'ordre : à égalité de reste, le centime va au premier. */
  payeurs: readonly Payeur[];
}

export interface Famille {
  id: string;
  statut: StatutTarifaire;
}

/** Ce que l'administration saisit pour un mois d'une école. */
export interface MoisAFacturer {
  /** `AAAA-MM`, le mois de la facture (septembre pour la semaine d'août). */
  mois: string;
  /** La période couverte, bornes comprises, `AAAA-MM-JJ`. */
  periode: { du: string; au: string };
  /** Les jours d'école du temps de midi (lundi, mardi, jeudi, vendredi). */
  joursDEcoleMidi: number;
  /** Une période offerte à l'intérieur de la période : ses présences ne se paient pas. */
  offert?: { du: string; au: string } | null;
}

export type TypeDeLigne = 'formule' | 'plage' | 'midi';

export interface LigneFacture {
  enfantId: string;
  type: TypeDeLigne;
  /** Le code de la formule ou de la plage ; `MIDI` pour le midi. */
  code: string;
  libelle: string;
  quantite: number;
  /** Au statut tarifaire du payeur de cette facture. */
  prixUnitaireCents: number;
  /** Toujours `quantite × prixUnitaireCents` : ce que vaut la ligne entière. */
  montantTotalCents: number;
  /** La part de ce payeur, en pour cent (100 quand il est seul). */
  partPct: number;
  /** Ce que ce payeur doit pour cette ligne : sa part du total, arrondie au centime. */
  montantCents: number;
  /** Les dates comptées, pour une ligne de plage. */
  dates: readonly string[];
}

export interface Avertissement {
  enfantId: string;
  code:
    | 'plage-inconnue'
    | 'formule-inconnue'
    | 'presence-hors-periode'
    | 'presence-hors-jours'
    | 'presence-en-double'
    | 'payeurs-invalides'
    | 'payeur-inconnu';
  detail: string;
}

export interface FactureCalculee {
  familleId: string;
  mois: string;
  lignes: readonly LigneFacture[];
  totalCents: number;
  avertissements: readonly Avertissement[];
}

/* ------------------------------------------------------------------------- */

/** Le prix dû selon le statut. `professionnel` ne paie rien. */
export function prixSelonStatut(prix: Prix, statut: StatutTarifaire): number {
  if (statut === 'professionnel') return 0;
  if (statut === 'reduit' && prix.reduitCents !== null) return prix.reduitCents;
  return prix.pleinCents;
}

/** Le jour de la semaine d'une date `AAAA-MM-JJ`, 1 = lundi … 7 = dimanche. */
export function jourDeLaSemaine(date: string): number {
  const [a, m, j] = date.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j)).getUTCDay();
  return d === 0 ? 7 : d;
}

/** Le prix d'une plage un jour donné. */
export function prixDeLaPlage(plage: Plage, date: string, statut: StatutTarifaire): number {
  const jour = jourDeLaSemaine(date) as JourOuvrable;
  const prix = plage.prixParJour?.[jour] ?? plage.prix;
  return prixSelonStatut(prix, statut);
}

/**
 * Répartit un montant en centimes selon des parts en pour cent dont la somme
 * fait 100 : plus fort reste, le centime en trop au premier à égalité. La
 * somme des parts rendues fait toujours le total.
 */
export function repartir(totalCents: number, partsPct: readonly number[]): number[] {
  const bruts = partsPct.map((p) => (totalCents * p) / 100);
  const planchers = bruts.map(Math.floor);
  let reste = totalCents - planchers.reduce((s, v) => s + v, 0);
  const ordre = bruts
    .map((b, i) => ({ i, frac: b - Math.floor(b) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of ordre) {
    if (reste <= 0) break;
    planchers[i] += 1;
    reste -= 1;
  }
  return planchers;
}

/** Les payeurs qui reçoivent une facture : ceux dont la part n'est pas 0. */
export function payeursActifs(payeurs: readonly Payeur[]): Payeur[] {
  return payeurs.filter((p) => p.partPct !== 0);
}

/** Les parts d'un enfant sont valides : hors payeurs à 0 %, des entiers de 1 à 100 dont la somme fait 100. */
export function payeursValides(payeurs: readonly Payeur[]): boolean {
  const actifs = payeursActifs(payeurs);
  if (actifs.length === 0) return false;
  if (actifs.some((p) => !Number.isInteger(p.partPct) || p.partPct < 1 || p.partPct > 100)) return false;
  return actifs.reduce((s, p) => s + p.partPct, 0) === 100;
}

const dansLIntervalle = (date: string, du: string, au: string) => date >= du && date <= au;

/**
 * Les présences d'un enfant qui comptent pour la facture : dédoublonnées,
 * dans la période, hors période offerte, et sans la plage impliquée par une
 * autre le même jour. Les avertissements décrivent ce qui a été écarté.
 */
export function presencesFacturables(
  enfant: Enfant,
  catalogue: CatalogueEcole,
  mois: MoisAFacturer,
): { retenues: Presence[]; avertissements: Avertissement[] } {
  const avertissements: Avertissement[] = [];
  const parCode = new Map(catalogue.plages.map((p) => [p.code, p]));
  const vues = new Set<string>();
  const uniques: Presence[] = [];

  for (const p of enfant.presences) {
    const cle = `${p.date}|${p.plage}`;
    if (vues.has(cle)) {
      avertissements.push({ enfantId: enfant.id, code: 'presence-en-double', detail: cle });
      continue;
    }
    vues.add(cle);
    const plage = parCode.get(p.plage);
    if (!plage) {
      avertissements.push({ enfantId: enfant.id, code: 'plage-inconnue', detail: cle });
      continue;
    }
    if (!dansLIntervalle(p.date, mois.periode.du, mois.periode.au)) {
      avertissements.push({ enfantId: enfant.id, code: 'presence-hors-periode', detail: cle });
      continue;
    }
    if (!plage.jours.includes(jourDeLaSemaine(p.date) as JourOuvrable)) {
      /* Signalée, mais comptée : quelqu'un l'a pointée, l'administration tranche. */
      avertissements.push({ enfantId: enfant.id, code: 'presence-hors-jours', detail: cle });
    }
    uniques.push(p);
  }

  /* `ATL2` implique `ATL1` : le même jour, l'impliquée ne se paie pas. */
  const impliquees = new Set<string>();
  for (const p of uniques) {
    const implique = parCode.get(p.plage)?.implique;
    if (implique) impliquees.add(`${p.date}|${implique}`);
  }

  const retenues = uniques.filter((p) => {
    if (impliquees.has(`${p.date}|${p.plage}`)) return false;
    if (mois.offert && dansLIntervalle(p.date, mois.offert.du, mois.offert.au)) return false;
    return true;
  });

  return { retenues, avertissements };
}

/** Une ligne entière, avant répartition : ce que vaut l'enfant à un statut donné. */
interface LigneEntiere {
  type: TypeDeLigne;
  code: string;
  libelle: string;
  quantite: number;
  prixUnitaireCents: number;
  dates: string[];
}

/** Les lignes entières d'un enfant, au statut tarifaire donné. */
function lignesEntieres(
  enfant: Enfant,
  statut: StatutTarifaire,
  catalogue: CatalogueEcole,
  mois: MoisAFacturer,
  retenues: readonly Presence[],
  formule: Formule | null,
): LigneEntiere[] {
  const lignes: LigneEntiere[] = [];

  if (formule) {
    lignes.push({
      type: 'formule',
      code: formule.code,
      libelle: formule.libelle,
      quantite: 1,
      prixUnitaireCents: prixSelonStatut(formule.prixMensuel, statut),
      dates: [],
    });
  }

  /* Les présences hors formule, groupées par plage et par prix unitaire. */
  const couvertes = new Set(formule?.plages ?? []);
  const groupes = new Map<string, { plage: Plage; prix: number; dates: string[] }>();
  for (const p of retenues) {
    if (couvertes.has(p.plage)) continue;
    const plage = catalogue.plages.find((x) => x.code === p.plage)!;
    const prix = prixDeLaPlage(plage, p.date, statut);
    const cle = `${plage.code}|${prix}`;
    const g = groupes.get(cle) ?? { plage, prix, dates: [] };
    g.dates.push(p.date);
    groupes.set(cle, g);
  }
  /* L'ordre du catalogue, le prix normal avant le prix d'un jour particulier. */
  const rang = (code: string) => catalogue.plages.findIndex((x) => x.code === code);
  const tries = [...groupes.values()].sort((a, b) => {
    const r = rang(a.plage.code) - rang(b.plage.code);
    if (r !== 0) return r;
    const aSpecial = a.prix !== prixSelonStatut(a.plage.prix, statut) ? 1 : 0;
    const bSpecial = b.prix !== prixSelonStatut(b.plage.prix, statut) ? 1 : 0;
    return aSpecial - bSpecial || a.prix - b.prix;
  });
  for (const g of tries) {
    g.dates.sort();
    const special = g.prix !== prixSelonStatut(g.plage.prix, statut);
    lignes.push({
      type: 'plage',
      code: g.plage.code,
      libelle: special ? `${g.plage.libelle} (${nomDuJour(g.dates[0])})` : g.plage.libelle,
      quantite: g.dates.length,
      prixUnitaireCents: g.prix,
      dates: g.dates,
    });
  }

  /* Le midi, compté en jours d'école, pour qui reste et n'est pas couvert. */
  const midiCouvert = formule?.couvreMidi ?? false;
  if (!midiCouvert && !enfant.rentreLeMidi && mois.joursDEcoleMidi > 0) {
    lignes.push({
      type: 'midi',
      code: 'MIDI',
      libelle: 'Temps de midi',
      quantite: mois.joursDEcoleMidi,
      prixUnitaireCents: prixSelonStatut(catalogue.prixMidi, statut),
      dates: [],
    });
  }

  return lignes;
}

/**
 * Les lignes d'un enfant pour chacun de ses payeurs : au statut de chaque
 * payeur, puis sa part, arrondie au centime par le plus fort reste sur le
 * total de la ligne au statut de ce payeur.
 */
export function lignesDeLEnfant(
  enfant: Enfant,
  familles: ReadonlyMap<string, Famille>,
  catalogue: CatalogueEcole,
  mois: MoisAFacturer,
): { lignes: Map<string, LigneFacture[]>; avertissements: Avertissement[] } {
  const parFamille = new Map<string, LigneFacture[]>();
  const { retenues, avertissements } = presencesFacturables(enfant, catalogue, mois);

  let formule: Formule | null = null;
  if (enfant.formule !== null) {
    formule = catalogue.formules.find((f) => f.code === enfant.formule) ?? null;
    if (!formule) {
      avertissements.push({ enfantId: enfant.id, code: 'formule-inconnue', detail: enfant.formule });
    }
  }

  if (!payeursValides(enfant.payeurs)) {
    avertissements.push({
      enfantId: enfant.id,
      code: 'payeurs-invalides',
      detail: enfant.payeurs.map((p) => `${p.familleId}:${p.partPct}`).join(',') || 'aucun payeur',
    });
    return { lignes: parFamille, avertissements };
  }
  const actifs = payeursActifs(enfant.payeurs);
  const inconnu = actifs.find((p) => !familles.has(p.familleId));
  if (inconnu) {
    avertissements.push({ enfantId: enfant.id, code: 'payeur-inconnu', detail: inconnu.familleId });
    return { lignes: parFamille, avertissements };
  }

  const parts = actifs.map((p) => p.partPct);
  actifs.forEach((payeur, i) => {
    const famille = familles.get(payeur.familleId)!;
    const entieres = lignesEntieres(enfant, famille.statut, catalogue, mois, retenues, formule);
    const lignes = entieres.map((l): LigneFacture => {
      const total = l.quantite * l.prixUnitaireCents;
      return {
        enfantId: enfant.id,
        type: l.type,
        code: l.code,
        libelle: l.libelle,
        quantite: l.quantite,
        prixUnitaireCents: l.prixUnitaireCents,
        montantTotalCents: total,
        partPct: payeur.partPct,
        montantCents: repartir(total, parts)[i],
        dates: l.dates,
      };
    });
    parFamille.set(payeur.familleId, lignes);
  });

  return { lignes: parFamille, avertissements };
}

/**
 * Les factures d'un mois pour une école : une par famille qui paie au moins
 * une ligne, dans l'ordre des familles données ; les lignes dans l'ordre des
 * enfants donnés. Les avertissements d'un enfant vont à la facture de chacun
 * de ses payeurs (ou, s'il n'en a aucun de valide, à toutes les familles
 * citées dans ses payeurs).
 */
export function calculerFacturesDuMois(
  catalogue: CatalogueEcole,
  mois: MoisAFacturer,
  familles: readonly Famille[],
  enfants: readonly Enfant[],
): FactureCalculee[] {
  const index = new Map(familles.map((f) => [f.id, f]));
  const lignesParFamille = new Map<string, LigneFacture[]>();
  const avertissementsParFamille = new Map<string, Avertissement[]>();
  for (const f of familles) {
    lignesParFamille.set(f.id, []);
    avertissementsParFamille.set(f.id, []);
  }

  for (const enfant of enfants) {
    const r = lignesDeLEnfant(enfant, index, catalogue, mois);
    for (const [familleId, lignes] of r.lignes) lignesParFamille.get(familleId)!.push(...lignes);
    for (const p of enfant.payeurs) avertissementsParFamille.get(p.familleId)?.push(...r.avertissements);
  }

  return familles.map((f) => {
    const lignes = lignesParFamille.get(f.id)!;
    return {
      familleId: f.id,
      mois: mois.mois,
      lignes,
      totalCents: lignes.reduce((s, l) => s + l.montantCents, 0),
      avertissements: avertissementsParFamille.get(f.id)!,
    };
  });
}

/** La facture d'une seule famille pour un mois, parmi des enfants dont elle est payeuse. */
export function calculerFacture(
  famille: Famille,
  catalogue: CatalogueEcole,
  mois: MoisAFacturer,
  enfants: readonly Enfant[],
  autresFamilles: readonly Famille[] = [],
): FactureCalculee {
  const toutes = [famille, ...autresFamilles.filter((f) => f.id !== famille.id)];
  return calculerFacturesDuMois(catalogue, mois, toutes, enfants).find((f) => f.familleId === famille.id)!;
}

/** Le total par enfant d'une facture : ce que l'attestation fiscale de ce payeur lira. */
export function totalParEnfant(facture: FactureCalculee): Map<string, number> {
  const m = new Map<string, number>();
  for (const l of facture.lignes) m.set(l.enfantId, (m.get(l.enfantId) ?? 0) + l.montantCents);
  return m;
}

const NOMS_DES_JOURS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
export function nomDuJour(date: string): string {
  return NOMS_DES_JOURS[jourDeLaSemaine(date)];
}
